import { Button, Divider, message } from 'antd';
import React, { useState, useEffect } from 'react';
import { useAtom } from 'jotai';
import { useSelector } from 'react-redux';
import { aiAlertsCustomMetricsLimit } from '@src/store/common/selectors';
import { ArrowLeftOutlined } from '@ant-design/icons';
import {
  FLOW_METRIC_POPUP_TITLE,
  isAppModule,
  METRIC_POPUP_OK_TEXT,
  METRIC_POPUP_TITLE,
  PRODUCT_MODE_MAP,
} from '@utils/constants';
import { drawerMetricsAtom, fullSensitivityConfigAtom } from '../atoms';
import {
  CountText,
  FlexFill,
  FooterWrapper,
  RowWrapper,
  StyledNoFlow,
  ContentWrapper,
  RightPanel,
  AnchorWrapper,
  StyledAlert,
  StyledDrawer,
  StyledTitleHeader,
} from './styled';
import { getMetricCategoryOrder } from '@src/utils/metrics/utilities';
import useMetricCategories from '@src/hooks/metrics/useMetricCategories';
import { metricsStore } from '@globalStore/metricsStore';
import CheckboxGroup from './components/CheckboxGroup';
import ModalAnchor from './components/MetricAnchor';
import { useRefTags } from '@src/hooks';
import ModalTitleWithSearch from './components/ModalTitleSearch';
import useFlowMetrics from './hooks/useFlowMetrics';
import { isEmpty } from 'lodash';
import { prepareDefaultSensitivityConfig } from '../utils';
import { Notice } from './Notice';
import { AI_ALERT_STANDARD_METRICS_MAP } from '@src/components/ai-alerts/constants';
import useGlobalExcludeMetricCategories from '@src/hooks/metrics/useGlobalExcludeMetricCategories';
import { useCurrentLimitation } from '@src/hooks/useCurrentLimitation';

const ALERTABLE_ECO_FLOW_METRIC_TYPES = ['avg_complete_duration', 'success', 'complete'];

const MetricsDrawer = ({
  isDrawerOpen,
  onBack,
  onClose,
  searchRef,
  setOpenMetricDrawer,
  selectedProduct,
}) => {
  const [drawerMetrics, setDrawerMetrics] = useAtom(drawerMetricsAtom);
  const [isLoading, setIsLoading] = useState(false);
  const aiAlertsMetricsCustomMetricsLimit = useSelector(aiAlertsCustomMetricsLimit);
  const [sensitivityDetails, setSensitivityDetails] = useAtom(fullSensitivityConfigAtom);
  const { metricsMeta, flowConfigMeta, metricsCategoryMeta, metricsCategoryKeys } =
    metricsStore.getState();
  const currentMetricCategoryOrder = getMetricCategoryOrder(metricsCategoryMeta);
  const globalExcludeMetricsCategory = useGlobalExcludeMetricCategories([]);
  const currentMetricCategory = metricsCategoryKeys;
  const { refs, setRefFn } = useRefTags(currentMetricCategoryOrder);
  const [searchText, setSearchText] = useState('');
  const limitation = useCurrentLimitation();

  let excludeCategories: string[] = [];
  if (isAppModule) {
    excludeCategories = [
      ...(limitation.metrics?.excludeCategories || []),
      ...globalExcludeMetricsCategory,
      metricsCategoryKeys.ECO_AUDIENCE,
      metricsCategoryKeys.ECO_SERVICE_PERFORMANCE,
      metricsCategoryKeys.ECO_ENGAGEMENT,
      metricsCategoryKeys.ECO_QOE,
      metricsCategoryKeys.ECO_WEB_USER_EXPERIENCE,
      metricsCategoryKeys.ECO_APP_USER_EXPERIENCE,
    ];
  }
  const unAlertableMetrics = Object.values(metricsMeta.app)
    .filter((m: any) => !m.isAlertable) // all metrics that are not alertable
    .map((m: any) => m.name);

  const metricCategories: any = useMetricCategories({
    supportedMetrics: metricsMeta.app,
    selectedMetricList: drawerMetrics,
    lockedMetricsSet: new Set(unAlertableMetrics),
    excludeCategories,
    allMetrics: metricsMeta.app,
  });

  const unAlertableFlowMetrics: string[] = Object.values(flowConfigMeta).reduce(
    (acc: string[], flow: any) => {
      if (!isEmpty(flow.metrics)) {
        Object.keys(flow.metrics).forEach(
          (key) => !ALERTABLE_ECO_FLOW_METRIC_TYPES.includes(key) && acc.push(flow.metrics[key]),
        );
      }
      return acc;
    },
    [],
  );

  const {
    flowMetricCategory,
    flowMetricCategories,
    flowMetricCategoryOrder,
    flowMetricCategoryKeys,
  } = useFlowMetrics({
    selectedMetricList: drawerMetrics,
    lockedMetricsSet: new Set(unAlertableFlowMetrics),
  });

  const onApply = () => {
    setIsLoading(true);

    const aiAlertsMappingKeys = AI_ALERT_STANDARD_METRICS_MAP[selectedProduct];
    const sensitivityForCustomMetrics = drawerMetrics.map((metricKey) => {
      const sensitivityConfig = sensitivityDetails.find(
        (sensitivity: any) => sensitivity.metricKey === metricKey,
      );
      if (sensitivityConfig) {
        return sensitivityConfig;
      } else {
        return prepareDefaultSensitivityConfig(metricKey, PRODUCT_MODE_MAP.APP);
      }
    });
    const filteredSensitivity = sensitivityDetails.filter((sensitivity: any) => {
      return aiAlertsMappingKeys.includes(sensitivity.metricKey);
    });

    setSensitivityDetails([...filteredSensitivity, ...sensitivityForCustomMetrics]);

    setTimeout(() => {
      setIsLoading(false);
      setOpenMetricDrawer(false);
    }, 1000);
  };

  const getInputListFn = (metricsList: any = []) => {
    if (searchText) {
      const st = searchText.toLowerCase();
      return metricsList.filter((metric) => metric.name.toLowerCase().includes(st));
    }
    return metricsList;
  };

  const metricCount = drawerMetrics.length;
  const footerButtons = [
    <FooterWrapper key="footer-wrapper">
      <CountText>{`${metricCount} ${metricCount > 1 ? 'metrics' : 'metric'} selected`}</CountText>
      <FlexFill />
      <Button
        key="discard"
        onClick={onBack}
        style={{
          marginRight: '10px',
        }}
      >
        Cancel
      </Button>
      <Button type="primary" key="save" loading={isLoading} onClick={onApply}>
        {METRIC_POPUP_OK_TEXT}
      </Button>
    </FooterWrapper>,
  ];

  const shouldShowFlow = isAppModule;

  const onChange = (item) => {
    let listOfMetrics: string[] = [];
    const metricName = item.target.value;
    listOfMetrics.push(...drawerMetrics);
    if (listOfMetrics.includes(metricName)) {
      listOfMetrics = listOfMetrics.filter((value) => value !== metricName);
    } else {
      listOfMetrics.push(metricName);
    }
    setDrawerMetrics(listOfMetrics);
  };

  // skip excluded categories
  const categoryOrder = currentMetricCategoryOrder.filter(
    (categoryKey) => currentMetricCategory[categoryKey] in metricCategories,
  );

  useEffect(() => {
    if (drawerMetrics.length > aiAlertsMetricsCustomMetricsLimit) {
      message.warning(`You can select a maximum of ${aiAlertsMetricsCustomMetricsLimit} metrics.`);
      setDrawerMetrics((prev) => prev.slice(0, aiAlertsMetricsCustomMetricsLimit));
    }
  }, [drawerMetrics]);

  const title = (
    <span>
      <ArrowLeftOutlined onClick={onBack} style={{ paddingRight: '20px', fontSize: 20 }} />
      {shouldShowFlow ? FLOW_METRIC_POPUP_TITLE : METRIC_POPUP_TITLE}
    </span>
  );

  const titleWithSearch = (
    <ModalTitleWithSearch
      placeholder="Search metric"
      modalTitle={title}
      searchText={searchText}
      setSearchText={setSearchText}
      searchRef={searchRef}
    />
  );

  return (
    <StyledDrawer
      title={titleWithSearch}
      width={window.innerWidth > 1000 ? 1000 : window.innerWidth - 100}
      open={isDrawerOpen}
      onClose={onClose}
      footer={footerButtons}
      forceRender={true}
    >
      <ContentWrapper>
        <AnchorWrapper>
          <StyledTitleHeader>
            <h3>Metrics</h3>
          </StyledTitleHeader>
          <ModalAnchor
            navRefs={refs.current}
            categoryOrder={categoryOrder}
            listCategory={metricsCategoryMeta}
            style={{ backgroundColor: '#F6F6F6' }}
          />
          {shouldShowFlow && (
            <>
              <Divider />
              <StyledTitleHeader>
                <h3>Flows</h3>
              </StyledTitleHeader>
              {flowMetricCategoryOrder && flowMetricCategoryOrder.length === 0 ? (
                <StyledNoFlow>No flow available</StyledNoFlow>
              ) : null}
              <ModalAnchor
                navRefs={refs.current}
                categoryOrder={flowMetricCategoryOrder}
                listCategory={flowMetricCategory}
              />
            </>
          )}
        </AnchorWrapper>
        <RightPanel>
          <StyledAlert message={<Notice />} type="info" />
          <h3>Metrics</h3>
          {categoryOrder.map((categoryKey) => {
            return (
              <RowWrapper key={categoryKey}>
                <CheckboxGroup
                  categoryKey={categoryKey}
                  listCategory={metricsCategoryMeta}
                  inputList={getInputListFn(metricCategories[currentMetricCategory[categoryKey]])}
                  onChange={onChange}
                  setRef={setRefFn(categoryKey)}
                  emptyText={<>No Metrics available</>}
                />
              </RowWrapper>
            );
          })}
          {shouldShowFlow && (
            <>
              <Divider />
              <h3>Flows</h3>
              {flowMetricCategoryOrder && flowMetricCategoryOrder.length === 0 && (
                <p>No flow available</p>
              )}
              {flowMetricCategoryOrder.map((categoryKey) => {
                return (
                  <RowWrapper key={categoryKey}>
                    <CheckboxGroup
                      categoryKey={categoryKey}
                      listCategory={flowMetricCategory}
                      inputList={getInputListFn(
                        flowMetricCategories[flowMetricCategoryKeys[categoryKey]],
                      )}
                      onChange={onChange}
                      setRef={setRefFn(categoryKey)}
                      emptyText={<>No metrics available</>}
                    />
                  </RowWrapper>
                );
              })}
            </>
          )}
        </RightPanel>
      </ContentWrapper>
    </StyledDrawer>
  );
};

export default MetricsDrawer;
